import { ApplicationRef, ChangeDetectorRef, Component, EventEmitter, HostBinding, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { CellClickedEvent, RowNode } from 'ag-grid-community';
import { isFunction } from '../../models/utils';
import { Comparison } from '../../models/comparison';
import { ContextMenuOption } from '../../models/context-menu-option';
import { Custodian } from '../../models/custodian';
import { DrRequestInfo } from '../../models/dr-request-info';
import { Endpoint, EntityType, HelpIndex, MessageType, UpdateStatusType, SearchType, TemplateOperationType } from '../../models/enums';
import { CellActionType, GenericColDef, GridContext, GridRowSelectionType } from '../../models/grid-context';
import { NavigationContentComponent } from '../../models/navigation-content-component';
import { Profile } from '../../models/profile';
import { SearchResult } from '../../models/search-result';
import { ViewType } from '../../models/view';
import { Workflow } from '../../models/workflow';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { GenericGridComponent } from '../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../generic-tree/generic-tree-node';
import { CreateEditComparisonModalComponent } from '../modals/create-edit-comparison-modal/create-edit-comparison-modal.component';
import { CreateEditEntityModalComponent } from '../modals/create-edit-entity-modal/create-edit-entity-modal.component';
import { EditBucketModalComponent, EditMode } from '../modals/edit-bucket/edit-bucket-modal.component';
import { ExportModalComponent } from '../modals/export-modal/export-modal.component';
import { GenericPickerModalComponent } from '../modals/generic-picker-modal/generic-picker-modal.component';
import { RunCombinedSearchModalComponent } from '../modals/run-combined-search-modal/run-combined-search-modal.component';
import { SummaryData, SummaryInfo } from './summary-info/summary-info';
import { ActionType, ObjectActionListType, Permissions } from '../../models/permission';
import { DataSetModalComponent } from '../modals/data-set-modal/data-set-modal.component';
import { AddSharedDataSetModalComponent } from '../modals/add-shared-data-set-modal/add-shared-data-set-modal.component';
import { NewLoadFileModalComponent } from '../modals/new-load-file-modal/new-load-file-modal.component';
import { MediaID } from '../../models/mediaid';
import { Batch } from '../../models/batch';
import { ContextMenuComponent } from '../context-menu/context-menu.component';
import { SaveAsLoadFromTemplateComponent } from '../modals/settings-modal/save-as-load-from-template/save-as-load-from-template.component';
import { SharedState } from '../../models/corpus';

@Component({
    standalone: false,
    selector: 'res-summary',
    templateUrl: './summary.component.html',
    styleUrls: ['./summary.component.scss']
})
export class SummaryComponent implements NavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent) genericGrid: GenericGridComponent;
    @ViewChild('headerTemplate') headerTemplate: TemplateRef<any>;
    @ViewChild('importsMenu', { static: true }) importsContextMenu: ContextMenuComponent;

    summaryData: SummaryData;
    selectTreeNode = new EventEmitter<any>();
    selectedNode: GenericTreeNode;

    gridName: string;
    automationId: string;
    helpIndex: HelpIndex;
    updateStatusType: UpdateStatusType;

    columnDefs: Array<GenericColDef>;
    rowSelection: GridRowSelectionType;
    requestInfo: DrRequestInfo;
    gridContext: GridContext;

    pagerVisible: boolean;
    pagerDisabled: boolean;
    workflowsHeader: boolean;
    newEntityName = '';
    headerTemplateAdded: boolean;
    searchHistoryHeader: boolean;
    importsHeader: boolean;
    isReefReviewProject: boolean

    contextMenuOptions: Array<ContextMenuOption>;

    hasAddPermissions: boolean;
    hasExportPermissions: boolean;
    hasWorkflowTemplatePermission: boolean;
    projectParsingV1: boolean;

    importsMenuOptions = [
        new ContextMenuOption({
            name: 'New Data Set from Load File ...',
            iconClass: 'fa-plus',
            iconColor: 'color-green-04',
            click: () => this.commonService.mainContent.createThenNavigate(NewLoadFileModalComponent, { class: 'vertical-percent-100 horizontal-percent-100' })
        }),
        new ContextMenuOption({
            name: 'New Data Set for Short Message Format ...',
            iconClass: 'fa-plus',
            iconColor: 'color-green-04',
            click: () => this.commonService.mainContent.createThenNavigate(DataSetModalComponent, { modalData: { shortMessageFormat: true }, class: 'vertical-percent-100 horizontal-percent-100' })
        }),
        new ContextMenuOption({
            name: 'Add Shared Data Set ...',
            click: () => this.commonService.mainContent.createThenNavigate(AddSharedDataSetModalComponent, { class: 'vertical-percent-100 horizontal-percent-100' })
        })
    ]

    responseArrayCallback: (value: any) => void;

    cellClickedCallback = (rowNode: RowNode, action: CellActionType, event?: CellClickedEvent): void => {
        if (rowNode && rowNode.data && rowNode.data.handle && action) {
            switch (action) {
                case CellActionType.linkLabelClick: {
                    if (this.selectTreeNode && this.selectTreeNode.observers.length > 0 && this.selectedNode) {
                        this.selectTreeNode.emit(rowNode.data.handle);
                    }
                    break;
                }
            }
        }
    }

    cellDoubleClickedCallback = (rowNode: RowNode): void => {
        if (this.selectTreeNode && this.selectTreeNode.observers.length > 0 && this.selectedNode) {
            if (rowNode.data instanceof SearchResult) {
                this.selectTreeNode.emit({ handle: rowNode.data.handle, searchToSelect: rowNode.data });
            } else {
                this.selectTreeNode.emit(rowNode.data.handle);
            }
        }
    }

    constructor(private restService: RestService, private commonService: CommonService, private changeDetector: ChangeDetectorRef,
        private applicationRef: ApplicationRef) {
    }

    selectedTreeItemChanged = (node: GenericTreeNode): void => {
        this.projectParsingV1 = this.commonService.currentProject.attributes.find(a => a.name === 'parsingVersion')?.value === 'V1';
        this.workflowsHeader = false;
        this.headerTemplateAdded = true;
        this.searchHistoryHeader = false;
        this.importsHeader = false;
        this.hasExportPermissions = Permissions.checkUserPermission(ObjectActionListType.EXPORT_STREAM);
        this.hasWorkflowTemplatePermission = Permissions.checkUserPermission(ObjectActionListType.ORG_WORKFLOWS);
        if (node && node.entityType && SummaryInfo.has(node.entityType)) {
            switch (node.entityType) {
                case EntityType.export:
                case EntityType.profile:
                case EntityType.comparison:
                case EntityType.folder:
                case EntityType.custodian:
                case EntityType.mediaID:
                case EntityType.batch:
                case EntityType.tagView:
                case EntityType.workFlow:
                    this.workflowsHeader = true;
                    this.newEntityName = node.entityType === EntityType.tagView ? 'tag' : (node.entityType === EntityType.export ? 'export stream' : (node.entityType === 'mediaID' ? 'MediaID' : (node.entityType === 'profile' ? 'Synthetic Document' : node.entityType)));
                    this.isReefReviewProject = node?.isReefReviewProject;
                    break;
                case EntityType.profileHistory:
                    this.headerTemplateAdded = false;
                    break;
                case EntityType.searchResult:
                    this.searchHistoryHeader = true;
                    break;
                case EntityType.import:
                    this.importsHeader = true;
                    break;
            }
            this.summaryData = SummaryInfo.get(node.entityType)(node);
            if (node.permissions && node.permissions.length > 0) {
                this.hasAddPermissions = Permissions.checkUserPermissions([{ permissionType: node.permissions[0].permissionType, permissionActions: [ActionType.CREATE] }]);
            }
            Object.assign(this, this.summaryData);
            if (this.summaryData.isValid) {
                this.selectedNode = null;
                this.changeDetector.detectChanges();
                this.selectedNode = node;
            }
            if (this.summaryData && Array.isArray(this.summaryData.columnDefs)) {
                const sharedCol = this.summaryData.columnDefs.find(
                    colDef => colDef.headerName === 'Shared' && colDef.cellRendererParams
                );
                if (sharedCol) {
                    sharedCol.cellRendererParams.onToggle = this.onSharedToggle.bind(this);
                }
            }
        }
    }

    onSharedToggle(nextChecked: boolean, params: any) {
        const data = params?.data;
        if (data) {
            this.restService.post(Endpoint.corpusManager_updateCorpusData, {
                corpusName: data.name,
                description: data.description,
                sharedState: data.sharedState === SharedState.SHARED ? SharedState.PRIVATE : SharedState.SHARED,
                viewHandle: data.corpusViewHandle,
                representationTypes: [],
                updateContent: false
            }).then(response => {
                if (response) {
                    nextChecked = !nextChecked;
                    this.commonService.openSuccessToastNotification(data.sharedState === SharedState.SHARED ? `${data.name} has been unshared` : `${data.name} has been shared`);
                    this.commonService.sendMessage(MessageType.entityChanged, UpdateStatusType.CORPUS);
                }
            }, error => {
            });
        }
    }

    selectedItems(): string {
        if (this.genericGrid) {
            const count = this.genericGrid.selectedRows.length;
            if (count > 0) {
                if (this.genericGrid.allRowsSelected) {
                    return `All ${this.genericGrid.totalItems.toLocaleString()}`;
                }
                return `${count.toLocaleString()} on this page`;
            }
            return `${count.toLocaleString()}`;
        }
        return '0';
    }

    addNewEntity(type?: string): void {
        if (this.selectedNode) {
            switch (this.selectedNode.entityType) {
                case EntityType.export:
                    if (type && type === 'reefReview') {
                        this.commonService.mainContent.createThenNavigate(ExportModalComponent, { class: 'vertical-percent-100', isDRExport: this.selectedNode?.isReefReviewProject });
                    } else {
                        this.commonService.mainContent.createThenNavigate(ExportModalComponent, { class: 'vertical-percent-100' });
                    }
                    break;
                case EntityType.comparison:
                    this.commonService.mainContent.createThenNavigate(CreateEditComparisonModalComponent,
                        { modalData: new Comparison(), class: 'vertical-percent-100 horizontal-percent-100' });
                    break;
                case EntityType.profile:
                    this.commonService.mainContent.createThenNavigate(CreateEditEntityModalComponent,
                        { modalData: new Profile({ projectHandle: this.commonService.currentProject.handle }) }, 'profile');
                    break;
                case EntityType.custodian:
                    this.commonService.mainContent.createThenNavigate(CreateEditEntityModalComponent, { modalData: new Custodian({ priorityEnabled: false }) }, 'custodian');
                    break;
                case EntityType.mediaID:
                    this.commonService.mainContent.createThenNavigate(CreateEditEntityModalComponent, { modalData: new MediaID() }, 'mediaid');
                    break;
                case EntityType.batch:
                    this.commonService.mainContent.createThenNavigate(CreateEditEntityModalComponent, { modalData: new Batch() }, 'batch');
                    break;
                case EntityType.folder:
                    this.commonService.mainContent.createThenNavigate(EditBucketModalComponent, { modalData: { mode: EditMode.FOLDER } }, 'dynamicFolder');
                    break;
                case EntityType.tagView:
                    this.commonService.mainContent.createThenNavigate(EditBucketModalComponent, { modalData: { mode: EditMode.TAG } }, 'tag');
                    break;
                case EntityType.workFlow:
                    this.commonService.mainContent.createThenNavigate(CreateEditEntityModalComponent,
                        { modalData: new Workflow() }, 'workFlow');
                    break;
                case EntityType.import:
                    this.commonService.mainContent.createThenNavigate(DataSetModalComponent, { modalData: {}, class: 'vertical-percent-100 horizontal-percent-100' });
                    break;
            }
        }
    }

    loadFromTemplate(): void {
        this.commonService.openModal(SaveAsLoadFromTemplateComponent, {
            class: "vertical-percent-80",
            modalData: {
                operationType: TemplateOperationType.loadFrom,
                selectedItem: { handle: this.commonService.currentProject?.handle },
                templateType: EntityType.workFlow,
                isSystemContext: false,
                currentOrg: this.commonService.currentUser?.customerName,
            },
        });
    }

    isRunAsCombinedEnabled(): boolean {
        if (this.genericGrid && this.genericGrid.selectedRows) {
            const queryBasedSearches = [];
            for (const r of this.genericGrid.selectedRows) {
                // attrib used to identify non term based searches that have task type like a term based search (samples, near dups, search by profile)
                // noticed that this attrib is populated with document_reference_list or vector_compare_format for those searches
                const resultFileFormatAttrib = r.attributes ? r.attributes.find(a => a.name === 'resultFileFormat') : undefined;
                if (r.task === SearchType.COLLECTION_SIMILARITY || r.task === SearchType.VECTOR_SET_FIND_DUPLICATES
                    || r.task === SearchType.BULK || r.task === SearchType.METADATA_BULK || r.task === SearchType.DOCUMENT_FIND_DUPLICATES
                    || (r.task === SearchType.FREEFORM && resultFileFormatAttrib)) {
                    // it's a non query based search, need to check if you can replace all this with task !== SearchType.ADVANCED, SearchType.FREEFORM
                    return false;
                } else {
                    queryBasedSearches.push(r);
                }
            }
            return queryBasedSearches.length >= 2;
        }
        return false;
    }

    isExportComparisonReportEnabled(): boolean {
        return this.genericGrid && this.genericGrid.selectedRows.length === 1 &&
            this.genericGrid.selectedRows[0].viewType === ViewType.WORKING_SET_RESULT &&
            !this.genericGrid.selectedRows[0].exportComparison && this.genericGrid.selectedRows[0].task !== SearchType.BULK;
    }

    runAsCombinedSearch(): void {
        if (this.genericGrid && this.genericGrid.selectedRows) {
            if (this.genericGrid.selectedRows) {
                const taskHandles = this.genericGrid.selectedRows.map(r => r.handle);
                if (taskHandles && taskHandles.length >= 2) {
                    this.restService.post(Endpoint.taskManager_getTasks, { taskHandles: taskHandles }).then((response) => {
                        const queriesToSend = [];
                        if (response && response.tasks) {
                            response.tasks.forEach(t => {
                                if (t.attributes) {
                                    const query = t.attributes.find(a => a.name === 'searchquery');
                                    if (query && query.value) {
                                        if (!query.value.startsWith('(')) {
                                            query.value = `(${query.value})`;
                                        }
                                        queriesToSend.push(query.value);
                                    }
                                }
                            });
                        }
                        if (queriesToSend.length >= 2) {
                            this.commonService.openModal(RunCombinedSearchModalComponent, {
                                modalData: {
                                    queries: queriesToSend,
                                }, class: 'vertical-percent-100'
                            });
                        } else {
                            this.commonService.openInformationToastNotification('Not all selected searches have queries. Please select other searches.');
                        }
                    });
                }
            }
        }
    }

    createExportComparisonReport(): void {
        if (this.genericGrid && this.genericGrid.selectedRows) {
            this.commonService.openModal(GenericPickerModalComponent, { modalData: { entityType: 'export-comparison', items: this.genericGrid.selectedRows[0] } }).then(response => {
                if (response && response.modalResult && response.modalData && response.modalData.export.data.handle && response.modalData.selectedTreeItem) {
                    const data = {
                        viewHandle: response.modalData.selectedTreeItem,
                        exportStreamHandle: response.modalData.export.data.handle,
                        taskDescription: response.modalData.taskDescription,
                        generateSearchTermReport: response.modalData.generateSearchTermReport,
                        provideExactGeneratedDocSizes: response.modalData.provideExactGeneratedDocSizes
                    };
                    this.restService.post(Endpoint.groupViewManager_compareToExport, data).then((result) => {
                        if (result) {
                            this.commonService.sendMessage(MessageType.navigationRequest, [this.genericGrid.selectedRows[0], result, 'reports']);
                        }
                    });
                }
            });
        }
    }

    showImportsMenu(event: MouseEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
            let eventTarget = event.target as HTMLElement;
            eventTarget = eventTarget?.nodeName === 'I' ? eventTarget.parentElement : eventTarget;
            const parentElCoordinates = {
                x: eventTarget.getBoundingClientRect().left,
                y: eventTarget.getBoundingClientRect().top + eventTarget.getBoundingClientRect().height - 1
            }
            const node = {
                contextMenuOptions: this.importsMenuOptions,
                parentElCoordinates: parentElCoordinates
            }
            this.importsContextMenu.open(event, node);
        }
    }
}
